import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowLeft, Calendar, Clock, User, Share2, AlertTriangle } from 'lucide-react';

const TaxObligationsBlogPost = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-[#1a1a1a]">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&h=800&fit=crop)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/80 via-[#1a1a1a]/90 to-[#1a1a1a]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 pt-12">
          {/* Back Link */}
          <Link 
            to="/community" 
            className="inline-flex items-center text-white/60 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Community
          </Link>

          {/* Category */}
          <div className="mb-6">
            <span className="bg-[#8D6E63] text-white text-xs font-medium px-4 py-1.5 rounded-full">
              Tax & Compliance
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Mastering US Tax Obligations for Foreign Startups
          </h1>

          {/* Subtitle */}
          <p className="text-xl text-white/70 mb-8 max-w-3xl">
            A comprehensive guide to navigating the complexities of US tax requirements when expanding your business into the American market
          </p>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-white/60 text-sm">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              <span>Trueledger Team</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              <span>December 15, 2025</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              <span>8 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Lead Paragraph */}
        <div className="mb-12">
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light">
            Many international founders mistakenly believe that incorporating in Delaware or Wyoming is the finish line. In reality, incorporation is just the beginning of a highly complex compliance journey.
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 leading-relaxed mb-8">
            As Trueledger expands its own footprint into the United States, we consistently see foreign entrepreneurs confuse corporate structure with tax exposure. The US tax system evaluates your business based on its actual operational footprint, not just its legal paperwork. Here is how strategic financial leaders evaluate and manage US tax obligations for foreign-owned startups.
          </p>

          {/* Section 1 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">1</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                The Myth of the "Tax-Free" US LLC
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <div className="bg-[#3E2723] text-white p-6 mb-6 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="w-6 h-6 text-[#8D6E63] mr-3 mt-0.5 flex-shrink-0" />
                  <p className="font-medium">
                    There is a dangerous misconception that if a foreign-owned US LLC has no US customers or physical operations, it is entirely exempt from IRS oversight. This is completely false.
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                For foreign owners, a single-member US LLC is typically treated as a "disregarded entity". While the LLC itself may not pay federal income tax if there are no US operations, the IRS still demands strict visibility through informational filings.
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="bg-stone-50 border-l-4 border-[#8D6E63] p-5">
                  <p className="text-gray-700">
                    <span className="font-semibold text-[#3E2723]">Form 5472 Requirement:</span> Every foreign-owned single-member LLC must file Form 5472 alongside a pro forma Form 1120 to report any transactions between the company and its foreign owner.
                  </p>
                </div>
                <div className="bg-stone-50 border-l-4 border-[#3E2723] p-5">
                  <p className="text-gray-800 font-semibold">
                    Failing to file a timely or complete Form 5472 triggers an automatic base penalty of $25,000 per year.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">2</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                ECI: The Trigger for Actual Tax Liability
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-6">
                Informational reporting is one thing, but actual tax liability is triggered by <span className="font-semibold text-[#3E2723]">Effectively Connected Income (ECI)</span>. ECI occurs when your startup is legally engaged in a US trade or business.
              </p>
              
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-6 mb-6">
                <h4 className="font-bold text-[#3E2723] mb-4">ECI Triggers Include:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#8D6E63] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700">Renting a US office, utilizing a US warehouse, or setting up a fulfillment center</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#8D6E63] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700">Hiring US-based employees or having an active partner on US soil</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-stone-50 border-l-4 border-[#3E2723] p-6">
                <p className="text-gray-800 font-medium italic">
                  "Once an LLC generates ECI, the foreign owner becomes personally liable for US income tax and must file Form 1040-NR (Non-Resident Alien Income Tax Return)."
                </p>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">3</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Entity Selection: C-Corp vs. LLC
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <div className="bg-stone-50 border-l-4 border-[#8D6E63] p-6 mb-6">
                <p className="text-gray-800 font-medium">
                  A strategic CFO does not choose an entity based on filing fees; they choose it based on capital strategy and tax modeling.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-stone-50 border border-stone-200 p-6 rounded-lg">
                  <h4 className="font-bold text-[#3E2723] mb-3">LLC Pass-Through</h4>
                  <p className="text-gray-600 text-sm mb-3">
                    Under Section 1446, foreign non-corporate partners are subject to a steep withholding rate on their share of ECTI.
                  </p>
                  <div className="bg-[#3E2723] text-white px-4 py-2 rounded text-center">
                    <span className="text-2xl font-bold">37%</span>
                    <span className="text-sm ml-2">Withholding Rate</span>
                  </div>
                </div>
                <div className="bg-stone-50 border border-stone-200 p-6 rounded-lg">
                  <h4 className="font-bold text-[#3E2723] mb-3">C-Corporation</h4>
                  <p className="text-gray-600 text-sm mb-3">
                    Pays a flat federal corporate tax rate, with standardized share structures for investors.
                  </p>
                  <div className="bg-[#8D6E63] text-white px-4 py-2 rounded text-center">
                    <span className="text-2xl font-bold">21%</span>
                    <span className="text-sm ml-2">Corporate Tax Rate</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#3E2723] text-white p-6 rounded-lg">
                <p className="font-medium">
                  Venture capital firms almost exclusively invest in C-Corps due to their standardized share structures and to avoid the complex individual flow-through tax scenarios for their Limited Partners.
                </p>
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">4</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                The Expanding Compliance Landscape
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-6">
                The IRS and FinCEN are constantly tightening the net on foreign-owned assets. True operational readiness means preparing for strict state and federal deadlines.
              </p>
              
              {/* State-Level Nuances */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">State-Level Nuances</h3>
                <p className="text-gray-600 mb-4">
                  Compliance varies heavily by entity type. Here's what you need to know about Delaware:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-stone-50 border border-stone-200 p-5 rounded-lg">
                    <h4 className="font-bold text-[#3E2723] mb-2">Delaware Corporations</h4>
                    <p className="text-gray-600 text-sm mb-2">Must file Annual Report and pay Franchise Tax</p>
                    <div className="flex items-center text-[#8D6E63] font-semibold">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Due: March 1st</span>
                    </div>
                  </div>
                  <div className="bg-stone-50 border border-stone-200 p-5 rounded-lg">
                    <h4 className="font-bold text-[#3E2723] mb-2">Delaware LLCs</h4>
                    <p className="text-gray-600 text-sm mb-2">No annual report; flat $300 Franchise Tax</p>
                    <div className="flex items-center text-[#8D6E63] font-semibold">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Due: June 1st</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* FBAR Requirements */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">FBAR Requirements</h3>
                <div className="bg-stone-50 border-l-4 border-[#8D6E63] p-5 mb-4">
                  <p className="text-gray-700">
                    A US disregarded entity is considered a <span className="font-semibold text-[#3E2723]">US person</span> for FinCEN reporting.
                  </p>
                </div>
                <p className="text-gray-600 mb-4">
                  If your US LLC holds foreign financial accounts that exceed $10,000 in aggregate value at any point during the year, you are required to file FinCEN Form 114 (FBAR).
                </p>
                <div className="bg-[#3E2723] text-white p-6 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="w-6 h-6 text-[#8D6E63] mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold mb-2">FBAR Penalty Warning</p>
                      <p className="text-white/90">
                        The civil penalty for willfully failing to file an FBAR can be as high as <span className="font-bold text-[#8D6E63]">$100,000</span> or <span className="font-bold text-[#8D6E63]">50% of the account balance</span>, whichever is greater.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Takeaways Box */}
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-8 mt-12">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Key Takeaways</h3>
          <div className="space-y-3">
            {[
              "Incorporation is just the beginning—compliance is ongoing",
              "Foreign-owned LLCs must file Form 5472 ($25,000 penalty for non-compliance)",
              "ECI triggers actual US tax liability for foreign owners",
              "C-Corps offer a flat 21% rate vs. 37% withholding for LLCs",
              "VC firms strongly prefer C-Corp structures",
              "FBAR filing is required for accounts exceeding $10,000"
            ].map((item, index) => (
              <div key={index} className="flex items-start">
                <span className="w-6 h-6 bg-[#3E2723] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0 mt-0.5">
                  {index + 1}
                </span>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Share & CTA Section */}
        <div className="mt-16 pt-12 border-t border-stone-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Share this article:</span>
              <button className="p-2 bg-stone-100 rounded-full hover:bg-stone-200 transition-colors">
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <a href="mailto:hello@trueledger.com?subject=US%20Tax%20Compliance%20Inquiry&body=Hi%20Trueledger%20Team,%0A%0AI%20read%20your%20article%20on%20US%20tax%20obligations%20for%20foreign%20startups%20and%20would%20like%20to%20learn%20more%20about%20your%20services.%0A%0AThank%20you.">
              <button className="bg-[#3E2723] text-white px-8 py-3 rounded-lg hover:bg-[#5D4037] transition-colors">
                Get Expert Tax Guidance
              </button>
            </a>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default TaxObligationsBlogPost;
